import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";

export default function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setSession(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name || "friend"}!`);
      if (data.user.role === "admin") navigate("/admin");
      else navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Log in to your account"
      subtitle="Access your dashboard and winterizing signup."
      footer={
        <div className="flex flex-col gap-1">
          <span>
            New customer?{" "}
            <Link to="/" className="font-medium text-secondary underline">Get started</Link>
          </span>
        </div>
      }
    >
      <form onSubmit={submit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="login-email-input" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)} className="h-11" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-muted-foreground underline" data-testid="login-forgot-link">
              Forgot password?
            </Link>
          </div>
          <Input id="password" data-testid="login-password-input" type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)} className="h-11" />
        </div>
        <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="h-11 w-full rounded-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log In"}
        </Button>
      </form>
    </AuthLayout>
  );
}
